<template>
  <MainLayout />
  <ContextMenu />
</template>

<script lang="ts" setup>
import ContextMenu from '@/components/ContextMenu/Menu.vue';
import { useContextMenuListener, useMouseClickListener } from '@/components/ContextMenu/hooks/useContextMenu';
import MainLayout from '@/components/Layout/Layout.vue';
import { onMounted, onUnmounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { ElNotification } from 'element-plus';
import { chatStore } from '@/store/chatStore';
import useGetGlobalProperties from './lib/hooks/useGlobal';
import Cookies from 'js-cookie';
import { isWechatBrowser, requestBrowserNotificationPermission } from './lib/utils';
import { retryReconnect } from './lib/websocket';
import dayjs from 'dayjs';
import { reportMonitorInfo } from './lib/helpers/platform';

const chatStoreEx = chatStore();
const {
  notificationMessageInfo,
  currentChatRoomAISpeechInfo,
  msgSendMsgFailedQueue,
  currentChatRoomMsgList,
  accessSessionParams,
} = storeToRefs(chatStoreEx);
const { $route, $router } = useGetGlobalProperties();
const checkPageLoad = () => {
  let whiteList = ['/payment', '/mBindAccount', '/bindAccount', '/webview', '/officialToMini'];
  if (whiteList.includes(location.pathname)) return;
  if ($route.fullPath === '/' && !Cookies.get('IM-Token') && !Cookies.get('reset_token')) {
    $router.replace(isWechatBrowser ? '/mLogin' : '/login');
  }
};
const initQuickReplyList = () => {
  const list = JSON.parse(window.localStorage.getItem('quickReplyList') || '[]');
  chatStoreEx.setQuickReplyList(list);
};
let reportTimer = null;
onMounted(() => {
  initQuickReplyList();
  checkPageLoad();
  useContextMenuListener();
  useMouseClickListener();
  if (!isWechatBrowser) {
    browserHandler();
    window.addEventListener('click', requestNotifyPermissionOnce, { once: true });
  }
  handleMessageFocus();
  handleRecommendSpeech();
  handleMessageFailedCheck();
  window.addEventListener('offline', function (e) {
    chatStoreEx.updateNetWorkStatus('offline');
  });
  window.addEventListener('online', function (e) {
    chatStoreEx.updateNetWorkStatus('online');
    retryReconnect();
  });
  window.addEventListener('mousedown', handlerButtonBlur);
  document.addEventListener('visibilitychange', handlerVisibleChange);
  // 进入后台上报
  reportTimer && clearTimeout(reportTimer);
  reportTimer = setTimeout(() => {
    reportMonitorInfo('$ClientStataus', {
      $module: 'user_open',
      $title: '客户端用户打开后台',
    });
  }, 500);
});
onUnmounted(() => {
  window.removeEventListener('mousedown', handlerButtonBlur);
  document.removeEventListener('visibilitychange', handlerVisibleChange);
  intervalTimer && clearInterval(intervalTimer);
});
const handlerVisibleChange = () => {
  if (document.visibilityState === 'hidden') {
    reportMonitorInfo('$ClientStatus', {
      $module: 'user_background_cutting',
      $title: '客户端用户切换到后台',
      $last_msg: currentChatRoomMsgList.value?.length
        ? JSON.stringify(currentChatRoomMsgList.value[currentChatRoomMsgList.value.length - 1]?.data)
        : '',
    });
  } else {
    retryReconnect();
  }
};
let intervalTimer = null;
const handleMessageFailedCheck = () => {
  intervalTimer = setInterval(() => {
    // 检查发送消息是否超过5秒没有回调，上报处理
    msgSendMsgFailedQueue.value?.forEach((v) => {
      if (dayjs().unix() - v.time > 5) {
        reportMonitorInfo('$MessageSend', {
          $module: 'user_send_message_fail',
          $title: '用户消息发送失败',
          sessionid: v.session_id,
          server_id: v.receive_id,
          text: v.text,
        });
        chatStoreEx.delMsgSendMsgFailedQueue(v.content_id);
      }
    });
    // 检查消息白屏，接入会话5秒未展示消息
    if (accessSessionParams.value?.session_id && dayjs().unix() - accessSessionParams.value?.time > 5) {
      reportMonitorInfo('$ClientStatus', {
        $module: 'user_white_screen',
        $title: '客户端白屏',
        sessionid: accessSessionParams.value.session_id,
        server_id: accessSessionParams.value.receive_id,
      });
      // 清空接入会话参数
      chatStoreEx.setAccessSessionParams({});
    }
  }, 1000);
};
const handleMessageFocus = () => {
  // 消息提醒，定位页面
  window.onfocus = () => {
    if (notificationMessageInfo.value && $route.fullPath !== '/chat') {
      $router.push('/chat');
    }
    retryReconnect();
  };
};
// 点击系统通知写入 notificationMessageInfo 后，窗口已聚焦时 onfocus 不会触发，需主动跳转
watch(notificationMessageInfo, (v) => {
  if (v && $route.fullPath !== '/chat') {
    $router.push('/chat');
  }
});
const requestNotifyPermissionOnce = () => {
  requestBrowserNotificationPermission();
};
const browserHandler = () => {
  if (window.navigator.userAgent.toUpperCase().indexOf('CHROME') === -1) {
    ElNotification({
      title: '温馨提示',
      message: '请使用Chrome浏览器、Edge浏览器以获得最佳体验',
      duration: 0,
    });
  }
};
const handleRecommendSpeech = () => {
  let val = window.localStorage?.getItem('showRecommendSpeechType');
  if (val !== null) {
    chatStoreEx.setCurrentChatRoomAISpeechInfo({
      ...currentChatRoomAISpeechInfo.value,
      showRecommedSpeech: JSON.parse(val),
    });
    chatStoreEx.setShowRecommedSpeechType(JSON.parse(val));
  }
};
let buttonBlurTimer;
const handlerButtonBlur = () => {
  buttonBlurTimer && clearTimeout(buttonBlurTimer);
  buttonBlurTimer = setTimeout(() => {
    const target = document.activeElement;
    if (target?.localName === 'button') {
      target?.blur();
    }
  });
};
</script>

<style lang="less">
@import url('@/public/public.less');
*,
:after,
:before {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
html,
body {
  scroll-behavior: smooth;
}
#app {
  background-color: #304b66;
}
.el-button--primary:not(.is-link):focus {
  background-color: #409eff !important;
  border-color: #409eff !important;
  color: #fff !important;
}
.el-button--primary:not(.is-link):hover {
  background-color: #66b1ff !important;
  border-color: #66b1ff !important;
  color: #fff !important;
}
.el-button--primary.is-link:focus {
  color: #409eff !important;
}
.el-button--primary.is-link:hover {
  color: #409eff !important;
}
#id-qa-sdk-ball {
  z-index: 2000;
}
</style>
